import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TEMPLATE_COLUMNS } from './templateColumns'

// Per-row icon cluster: edit / trash / reorder. Default colour #4F5F4A for all three (mr-6 spec
// S5.3). React-state-driven hover, not CSS `:hover` -- jsdom cannot see a pseudo-class, and this
// feature folder's own house pattern (ScenarioRibbon.jsx's AddScenarioButton) already does it this
// way for the same reason.
const DEFAULT_COLOR = '#4F5F4A'
const EDIT_HOVER = '#328C12'
const DELETE_HOVER = '#D85263'
const DISABLED_COLOR = '#C6CFC6'

// Manage Templates drawings (letter 99, 2.2.4), taken from the 30.09 export's first row
// (#Frame_25, #Frame_26, #uil:sort_9) with the row offset subtracted, so each glyph sits in its own
// 14x14 box (sort: 10x14) exactly as drawn. Figma's default 1px stroke.
function ManageEditIcon({ color }) {
  const common = { stroke: color, strokeMiterlimit: 10, strokeLinecap: 'round', strokeLinejoin: 'round' }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M10.17 8.47V13.45H0.55V3.83H5.53" {...common} />
      <path d="M6.99 8.55L4.67 9.33L5.45 7.01L11.91 0.55L13.45 2.09L6.99 8.55Z" {...common} />
      <path d="M10.36 2.09L11.91 3.64" {...common} />
    </svg>
  )
}

function ManageTrashIcon({ color }) {
  const common = { stroke: color, strokeMiterlimit: 10, strokeLinecap: 'round', strokeLinejoin: 'round' }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M1.53 2.73H12.47M4.81 2.73C4.81 1.53 5.79 0.55 7 0.55C8.21 0.55 9.19 1.53 9.19 2.73M11.37 4.92V11.27C11.37 12.47 10.4 13.45 9.19 13.45H4.81C3.6 13.45 2.62 12.47 2.62 11.27V2.73"
        {...common}
      />
      <path d="M5.6 10.17V6.02M8.49 10.17V6.02" {...common} />
    </svg>
  )
}

// The design draws one uil:sort glyph; its two chevrons are split into two 10x7 buttons (up = move
// up, down = move down) so the drawing is unchanged and the reorder stays one click per step.
const SORT_UP_PATH =
  'M1.78 5.28L5 2.06L8.22 5.28C8.29 5.35 8.37 5.41 8.46 5.45C8.55 5.48 8.65 5.5 8.75 5.5C8.85 5.5 8.95 5.48 9.04 5.45C9.13 5.41 9.21 5.35 9.28 5.28C9.35 5.21 9.41 5.13 9.45 5.04C9.48 4.95 9.5 4.85 9.5 4.75C9.5 4.65 9.48 4.55 9.45 4.46C9.41 4.37 9.35 4.29 9.28 4.22L5.53 0.47C5.46 0.4 5.38 0.34 5.29 0.3C5.2 0.27 5.1 0.25 5 0.25C4.9 0.25 4.8 0.27 4.71 0.3C4.62 0.34 4.54 0.4 4.47 0.47L0.72 4.22C0.65 4.29 0.59 4.37 0.55 4.46C0.52 4.55 0.5 4.65 0.5 4.75C0.5 4.95 0.58 5.14 0.72 5.28C0.86 5.42 1.05 5.5 1.25 5.5C1.45 5.5 1.64 5.42 1.78 5.28Z'
// Down chevron, shifted up by the 7px the lower button starts at.
const SORT_DOWN_PATH =
  'M8.22 1.72L5 4.94L1.78 1.72C1.64 1.58 1.45 1.5 1.25 1.5C1.05 1.5 0.86 1.58 0.72 1.72C0.58 1.86 0.5 2.05 0.5 2.25C0.5 2.45 0.58 2.64 0.72 2.78L4.47 6.53C4.54 6.6 4.62 6.66 4.71 6.7C4.8 6.73 4.9 6.75 5 6.75C5.1 6.75 5.2 6.73 5.29 6.7C5.38 6.66 5.46 6.6 5.53 6.53L9.28 2.78C9.35 2.71 9.41 2.63 9.44 2.54C9.48 2.45 9.5 2.35 9.5 2.25C9.5 2.15 9.48 2.05 9.44 1.96C9.41 1.87 9.35 1.79 9.28 1.72C9.21 1.65 9.13 1.59 9.04 1.55C8.95 1.52 8.85 1.5 8.75 1.5C8.65 1.5 8.55 1.52 8.46 1.55C8.37 1.59 8.29 1.65 8.22 1.72Z'

function SortHalf({ direction, color }) {
  return (
    <svg width="10" height="7" viewBox="0 0 10 7" fill="none" aria-hidden="true">
      <path d={direction === 'up' ? SORT_UP_PATH : SORT_DOWN_PATH} fill={color} />
    </svg>
  )
}

// Hit areas: each button is exactly its glyph (so the drawn 8px spacing is the rendered one) and a
// transparent ::before widens the clickable area to 24x24 around it, 24x12 per sort half. Cluster
// x: edit 0..14, delete 22..36, sort 44..54 -> areas -7..17, 17..41, 41..65, touching, never
// overlapping (each split within 1px of the gap's midpoint; exact midpoints would leave delete 22 wide).
export const MANAGE_HIT_AREAS = {
  edit: "relative before:content-[''] before:absolute before:-top-[5px] before:-bottom-[5px] before:-left-[7px] before:-right-[3px]",
  delete: "relative before:content-[''] before:absolute before:-top-[5px] before:-bottom-[5px] before:-left-[5px] before:-right-[5px]",
  sortUp: "relative before:content-[''] before:absolute before:-top-[5px] before:bottom-0 before:-left-[3px] before:-right-[11px]",
  sortDown: "relative before:content-[''] before:absolute before:top-0 before:-bottom-[5px] before:-left-[3px] before:-right-[11px]",
}

const TOOLTIP_GAP = 8
const VIEWPORT_MARGIN = 8

// `delete-hover` (05.10 export): the panel's right edge on the row's (the cluster's) right edge, its
// bottom 8 above the trash icon; kept on-screen, and below the icon when there is no room above.
export function placeBlockedTooltip(clusterRect, iconRect, panel, viewport) {
  const left = Math.max(VIEWPORT_MARGIN, Math.min(clusterRect.right - panel.width, viewport.width - VIEWPORT_MARGIN - panel.width))
  const above = iconRect.top - TOOLTIP_GAP - panel.height
  return { left, top: above >= VIEWPORT_MARGIN ? above : iconRect.bottom + TOOLTIP_GAP }
}

// Letter 122 item 5: the blocked trash's message, on hover or keyboard focus at once (no native
// `title` delay). Portaled and fixed: the row list scrolls, and would clip a panel above its first
// row. README.md ("Blocked delete message").
function BlockedDeleteTooltip({ clusterRef, iconRef, message }) {
  const panelRef = useRef(null)
  const [pos, setPos] = useState(null)
  useLayoutEffect(() => {
    let frame = null
    const place = () => {
      frame = null
      const cluster = clusterRef.current
      const icon = iconRef.current
      const panel = panelRef.current
      if (!cluster || !icon || !panel) return
      const viewport = { width: window.innerWidth, height: window.innerHeight }
      setPos(placeBlockedTooltip(cluster.getBoundingClientRect(), icon.getBoundingClientRect(), panel.getBoundingClientRect(), viewport))
    }
    const schedule = () => {
      if (frame == null) frame = window.requestAnimationFrame(place)
    }
    place()
    document.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame)
      document.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
    }
  }, [clusterRef, iconRef])
  return createPortal(
    <div
      ref={panelRef}
      aria-hidden="true"
      data-testid="blocked-delete-tooltip"
      className="fixed z-[160] w-max max-w-[calc(100vw-16px)] rounded-[8px] bg-white px-[10px] py-[7px] text-left text-[13px] leading-[16px] font-normal text-brand-statusSale shadow-[0_0_10px_rgba(0,0,0,0.1)] pointer-events-none"
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
    >
      {message}
    </div>,
    document.getElementById('portal') ?? document.body
  )
}

// Manage Templates (letter 99, 2.2): edit, delete, sort at the far right, 8px apart.
// A blocked delete is `aria-disabled`, not `disabled`: it stays focusable and gets pointer events in
// every browser, so its message can show on hover and on Tab.
export default function RowIconCluster({ onEdit, onDelete, deletable, deleteTooltip, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const [editHovered, setEditHovered] = useState(false)
  const [deleteHovered, setDeleteHovered] = useState(false)
  // Shown while hovered or focused, until Escape (which holds until the next hover or focus).
  const [blockedHovered, setBlockedHovered] = useState(false)
  const [blockedFocused, setBlockedFocused] = useState(false)
  const [blockedDismissed, setBlockedDismissed] = useState(false)
  const clusterRef = useRef(null)
  const trashRef = useRef(null)
  const reasonId = useId()
  const blocked = !deletable
  const showBlocked = blocked && Boolean(deleteTooltip) && !blockedDismissed && (blockedHovered || blockedFocused)
  const trashColor = blocked ? (showBlocked ? DELETE_HOVER : DISABLED_COLOR) : deleteHovered ? DELETE_HOVER : DEFAULT_COLOR

  return (
    <div ref={clusterRef} className={`${TEMPLATE_COLUMNS.manageIcons} flex items-center gap-x-[8px] shrink-0`} data-testid="row-icons">
      <button
        type="button"
        onClick={onEdit}
        onMouseEnter={() => setEditHovered(true)}
        onMouseLeave={() => setEditHovered(false)}
        aria-label="Rename template"
        className={`${MANAGE_HIT_AREAS.edit} flex items-center justify-center w-[14px] h-[14px] p-0`}
      >
        <ManageEditIcon color={editHovered ? EDIT_HOVER : DEFAULT_COLOR} />
      </button>
      <button
        ref={trashRef}
        type="button"
        onClick={deletable ? onDelete : undefined}
        onMouseEnter={() => {
          if (deletable) return setDeleteHovered(true)
          setBlockedHovered(true)
          setBlockedDismissed(false)
        }}
        onMouseLeave={() => {
          setDeleteHovered(false)
          setBlockedHovered(false)
        }}
        onFocus={() => {
          setBlockedFocused(true)
          setBlockedDismissed(false)
        }}
        onBlur={() => setBlockedFocused(false)}
        // Only while the message shows: a second Escape still reaches the modal.
        onKeyDown={(e) => {
          if (e.key !== 'Escape' || !showBlocked) return
          e.stopPropagation()
          setBlockedDismissed(true)
        }}
        aria-disabled={blocked || undefined}
        aria-describedby={blocked && deleteTooltip ? reasonId : undefined}
        aria-label="Delete template"
        className={`${MANAGE_HIT_AREAS.delete} flex items-center justify-center w-[14px] h-[14px] p-0 aria-disabled:cursor-not-allowed`}
      >
        <ManageTrashIcon color={trashColor} />
      </button>
      {blocked && deleteTooltip ? (
        <span id={reasonId} className="sr-only">
          {deleteTooltip}
        </span>
      ) : null}
      {showBlocked ? <BlockedDeleteTooltip clusterRef={clusterRef} iconRef={trashRef} message={deleteTooltip} /> : null}
      <div className="flex flex-col w-[10px]">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          aria-label="Move template up"
          className={`${MANAGE_HIT_AREAS.sortUp} flex w-[10px] h-[7px] p-0 disabled:cursor-not-allowed`}
        >
          <SortHalf direction="up" color={canMoveUp ? DEFAULT_COLOR : DISABLED_COLOR} />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          aria-label="Move template down"
          className={`${MANAGE_HIT_AREAS.sortDown} flex w-[10px] h-[7px] p-0 disabled:cursor-not-allowed`}
        >
          <SortHalf direction="down" color={canMoveDown ? DEFAULT_COLOR : DISABLED_COLOR} />
        </button>
      </div>
    </div>
  )
}
